/**
 * A permission requirement, evaluated by `PermissionService.CheckPermission` and — for a routed screen — by
 * `AuthGuard` via {@link routeData}.
 *
 * **Two vocabularies meet in this class, and putting a value in the wrong one silently never matches:**
 *
 * - `_super` and {@link anyRoles} hold **central role names** (`Roles`, mirroring
 *   `CheqMe.Server.Contracts/Authorization/Roles.cs`) — the strings the context token actually stamps as role
 *   claims, and therefore what `user.permissions` contains for a grant-based session.
 * - `default` holds **legacy endpoint-permission ids** (`"Company.Employee.List"`, …) and is **AND**-evaluated:
 *   every id must be held. Legacy `User` tokens still carry these alongside the central roles.
 *
 * A grant-based session (`CompanyOwner`, `Payroll`, `Operator`, `ShiftLead`, …) holds *only* central roles, so
 * it matches no `default` list at all. That is why such a role has to be named in `_super` or {@link anyRoles}.
 */
export class PermissionSet {
  /** Legacy endpoint-permission ids. AND-evaluated: the session must hold every one of them. */
  default: string[] = [];

  /** The single central role that supersedes {@link default} outright (historically always `"ShardAdmin"`). */
  _super: string = "";

  /**
   * Further central roles, **any one** of which grants this set outright — an OR-list beside the single
   * `_super`. Exists because `_super` is one string and some surfaces are legitimately reachable by two
   * unrelated roles (e.g. the shift plan: `ShardAdmin` *or* `ShiftLead`, #280).
   *
   * Deliberately a *separate* field rather than extra entries in {@link default}: `default` is AND-evaluated,
   * so a central role dropped in there would become **required** of every session that reaches the surface
   * through the legacy granular ids — turning a widening into a lockout.
   */
  anyRoles: string[] = [];

  constructor(sup: string, def: string[], anyRoles: string[] = []) {
    this.default = def;
    this._super = sup;
    this.anyRoles = anyRoles;
  }

  /**
   * The `data` object an `AuthGuard`-protected route should carry to be gated on **this** set.
   *
   * Route and menu entry are then driven by the same object: the navbar asks
   * `PermissionService.CheckPermission(set)`, the route hands `AuthGuard` `set.routeData()`, and neither can
   * grow a role the other lacks. `roles` stays the AND-evaluated legacy list `AuthGuard` has always read;
   * `anyRoles` is the OR-list it short-circuits on.
   */
  routeData(): { roles: string[]; anyRoles: string[] } {
    return {roles: this.default, anyRoles: this.anyRoles};
  }
}
